import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  ImportControlModeTemplateResponseDto,
  ImportResultItem,
} from '@src/types/ControlModes';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import useAlias from '@src/hooks/useAlias';

interface ImportResultDialogProps {
  open: boolean;
  onClose: () => void;
  result: ImportControlModeTemplateResponseDto | null;
}

export default function ImportResultDialog({
  open,
  onClose,
  result,
}: ImportResultDialogProps): React.ReactElement {
  const alias = useAlias();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {},
  );

  if (!result) {
    return <Dialog open={false} onClose={onClose} />;
  }

  const total = result.results ? result.results.length : 0;

  const handleToggleItem = (name: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <FormattedAlias id='Import Results' />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant='body1' gutterBottom>
            <FormattedAlias id='The import operation has been completed successfully.' />
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant='body1' fontWeight='bold'>
            <FormattedAlias id='Total templates processed:' />
          </Typography>
          <Typography variant='body1'>{total}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant='body1' color='success.main' fontWeight='bold'>
            <FormattedAlias id='Templates added:' />
          </Typography>
          <Typography variant='body1' color='success.main'>
            {result.updated}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant='body1' color='warning.main' fontWeight='bold'>
            <FormattedAlias id='Templates updated:' />
          </Typography>
          <Typography variant='body1' color='warning.main'>
            {result.added}
          </Typography>
        </Box>

        {result.results && result.results.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant='h6' gutterBottom>
              <FormattedAlias id='Detailed Results' />
            </Typography>
            <List>
              {result.results.map((item: ImportResultItem, index: number) => (
                <React.Fragment key={index}>
                  <ListItem
                    secondaryAction={
                      <IconButton
                        edge='end'
                        onClick={() => handleToggleItem(item.name)}
                      >
                        {expandedItems[item.name] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={item.name}
                      secondary={`Status: ${item.status}`}
                      slotProps={{
                        primary: {
                          fontWeight: 'bold',
                          color:
                            item.status === 'added'
                              ? 'success.main'
                              : item.status === 'updated'
                                ? 'warning.main'
                                : 'text.secondary',
                        },
                      }}
                    />
                  </ListItem>
                  <Collapse
                    in={expandedItems[item.name]}
                    timeout='auto'
                    unmountOnExit
                  >
                    <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                      <Typography variant='body2' color='text.secondary'>
                        {alias('ID')}: {item.id}
                      </Typography>
                    </Box>
                  </Collapse>
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary' variant='contained'>
          <FormattedAlias id='Close' />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
